import { CommonModule } from '@angular/common';
import { Component, OnInit, inject, ChangeDetectorRef } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { NzModalModule, NzModalService } from 'ng-zorro-antd/modal';
import { NzTableModule } from 'ng-zorro-antd/table';
import { firstValueFrom, of } from 'rxjs';
import { catchError, timeout } from 'rxjs/operators';
import { SubuserPermissionsService, ClientRoleItem, PolicySummary } from '@services/subuser-permissions.service';
import { SharedService } from '@services/shared.service';
import { SecureStorageService } from '@services/secure-storage.service';

export interface DisplayRoleCard extends ClientRoleItem {
  icon?: string;
  color?: string;
  memberCount?: number;
  /** Pages this role grants view on, from the shared policy snapshot. */
  grantedPages?: number;
  totalPages?: number;
}

@Component({
  selector: 'app-roles',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    TranslateModule,
    NzModalModule,
    NzTableModule,
  ],
  templateUrl: './roles.component.html',
  styleUrls: ['./roles.component.scss'],
})
export class RolesComponent implements OnInit {
  private permissionsService = inject(SubuserPermissionsService);
  private sharedService = inject(SharedService);
  private secureStorage = inject(SecureStorageService);
  private router = inject(Router);
  private modal = inject(NzModalService);
  private cdr = inject(ChangeDetectorRef);
  private translateService = inject(TranslateService);

  isLoading = false;
  isSaving = false;
  searchTerm = '';

  roles: DisplayRoleCard[] = [];
  allSubUsers: any[] = [];

  // Create Role Modal
  isCreateModalVisible = false;
  newRoleName = '';
  newRoleDescription = '';
  inlineNameError = '';

  // Edit Role Modal
  isEditModalVisible = false;
  editingRole: DisplayRoleCard | null = null;
  editRoleName = '';
  editRoleDescription = '';
  inlineEditNameError = '';
  isUpdating = false;

  // Delete Role Modal
  isDeleteModalVisible = false;
  deletingRole: DisplayRoleCard | null = null;
  isDeleting = false;

  summary: PolicySummary | null = null;

  private getParentId(): number {
    const userDetails = this.secureStorage.getItem<any>('userDetails');
    const id = Number(userDetails?.id || userDetails?.user_id || 0);
    // Same fallback the rest of Policy Management uses, kept in the service.
    return id > 0 ? id : this.permissionsService.getEffectiveParentId();
  }

  async ngOnInit(): Promise<void> {
    await this.loadData();
  }

  async loadData(): Promise<void> {
    this.isLoading = true;
    this.sharedService.showLoader();
    try {
      const parentId = this.getParentId();
      const [backendRoles, subUsersRes] = await Promise.all([
        firstValueFrom(
          this.permissionsService.getClientRoles().pipe(
            timeout(5000),
            catchError(() => of([]))
          )
        ),
        firstValueFrom(
          this.permissionsService.getSubUsersByParent(parentId, 1, 100).pipe(
            timeout(4500),
            catchError(() => of({ items: [] }))
          )
        ),
      ]);

      this.allSubUsers = subUsersRes?.items || subUsersRes?.data || (Array.isArray(subUsersRes) ? subUsersRes : []);

      // Whatever client_roles returns is what the client has. There is no
      // fallback preset list: this used to invent Administrator / Campaign
      // Manager / Developer rows whenever the call came back empty, which
      // showed three roles (with ids 1-3 and a sub-user count) that do not
      // exist on the server — and whose Configure / Edit / Delete actions
      // addressed ids the backend has never heard of. An empty list now falls
      // through to the table's own empty state, which offers Create.
      const roleRows = Array.isArray(backendRoles) ? backendRoles : [];

      // The shared snapshot decides both counts, so this screen and Roles
      // Management can no longer report different numbers for one role.
      this.summary = await firstValueFrom(
        this.permissionsService.getPolicySummary(parentId, true).pipe(
          timeout(15000),
          catchError(() => of(null as any))
        )
      );

      this.roles = roleRows.map((r, idx) => {
        const id = Number(r.id);
        const fromSummary = this.summary?.usersPerRole?.[id];
        return {
          ...r,
          icon: this.getRoleIcon(r.policy_name, idx),
          color: this.getRoleColor(idx),
          memberCount: fromSummary !== undefined
            ? fromSummary
            : this.allSubUsers.filter(
                (u) => this.permissionsService.getSubUserAssignedRole(u, roleRows) === id
              ).length,
          grantedPages: this.summary?.pagesPerRole?.[id] ?? 0,
          totalPages: this.summary?.pagesCount ?? 0,
        };
      });
    } catch (err) {
      console.warn('[RolesComponent] Failed to load roles directory data:', err);
    } finally {
      this.isLoading = false;
      this.sharedService.hideLoader();
      this.cdr.detectChanges();
    }
  }

  get filteredRoles(): DisplayRoleCard[] {
    const term = (this.searchTerm || '').toLowerCase().trim();
    return this.roles.filter((r) => {
      if (term) {
        const nameMatch = (r.policy_name || '').toLowerCase().includes(term);
        const descMatch = (r.description || '').toLowerCase().includes(term);
        if (!nameMatch && !descMatch) return false;
      }
      return true;
    });
  }

  openCreateModal(): void {
    this.newRoleName = '';
    this.newRoleDescription = '';
    this.inlineNameError = '';
    this.isCreateModalVisible = true;
  }

  closeCreateModal(): void {
    this.isCreateModalVisible = false;
    this.inlineNameError = '';
  }

  onRoleNameInput(): void {
    if (this.inlineNameError) {
      this.inlineNameError = '';
    }
  }

  async handleCreateRole(): Promise<void> {
    const name = this.newRoleName.trim();
    const desc = this.newRoleDescription.trim();

    if (!name) {
      this.inlineNameError = 'Role name is required.';
      return;
    }

    this.isSaving = true;
    this.inlineNameError = '';

    try {
      const res: any = await firstValueFrom(
        this.permissionsService.createClientRole(name, desc).pipe(
          timeout(6000)
        )
      );

      const createdId = res?.id || res?.role_id || res?.data?.id;
      this.sharedService.createNotification('success', `Role "${name}" created successfully!`);
      this.closeCreateModal();

      // Navigate to Roles Management to configure page permissions
      if (createdId) {
        this.router.navigate(['/home/roles-management'], { queryParams: { roleId: createdId } });
      } else {
        await this.loadData();
      }
    } catch (err: any) {
      console.warn('[RolesComponent] createClientRole error:', err);
      // Duplicate name returns an error message -> show it inline on the name field
      const serverMessage =
        err?.error?.message ||
        err?.error?.error ||
        err?.message ||
        'A role with this name already exists.';
      this.inlineNameError = serverMessage;
    } finally {
      this.isSaving = false;
      this.cdr.detectChanges();
    }
  }

  openEditModal(role: DisplayRoleCard, event?: MouseEvent): void {
    if (event) {
      event.stopPropagation();
    }
    this.editingRole = role;
    this.editRoleName = role.policy_name || '';
    this.editRoleDescription = role.description || '';
    this.inlineEditNameError = '';
    this.isEditModalVisible = true;
  }

  closeEditModal(): void {
    this.isEditModalVisible = false;
    this.editingRole = null;
    this.inlineEditNameError = '';
  }

  onEditRoleNameInput(): void {
    if (this.inlineEditNameError) {
      this.inlineEditNameError = '';
    }
  }

  async handleUpdateRole(): Promise<void> {
    if (!this.editingRole) return;
    const name = this.editRoleName.trim();
    const desc = this.editRoleDescription.trim();

    if (!name) {
      this.inlineEditNameError = 'Role name is required.';
      return;
    }

    this.isUpdating = true;
    this.inlineEditNameError = '';

    try {
      await firstValueFrom(
        this.permissionsService.updateClientRole(this.editingRole.id, name, desc).pipe(
          timeout(6000)
        )
      );

      this.sharedService.createNotification('success', `Role "${name}" updated successfully!`);

      // Update local card immediately
      this.editingRole.policy_name = name;
      this.editingRole.description = desc;

      this.closeEditModal();
    } catch (err: any) {
      console.warn('[RolesComponent] updateClientRole error:', err);
      const serverMessage =
        err?.error?.message ||
        err?.error?.error ||
        err?.message ||
        'Failed to update role.';
      this.inlineEditNameError = serverMessage;
    } finally {
      this.isUpdating = false;
      this.cdr.detectChanges();
    }
  }

  openDeleteModal(role: DisplayRoleCard, event?: MouseEvent): void {
    if (event) {
      event.stopPropagation();
    }
    this.deletingRole = role;
    this.isDeleteModalVisible = true;
  }

  closeDeleteModal(): void {
    this.isDeleteModalVisible = false;
    this.deletingRole = null;
  }

  async handleDeleteRole(): Promise<void> {
    if (!this.deletingRole) return;

    const roleToDelete = this.deletingRole;
    this.isDeleting = true;

    try {
      await firstValueFrom(
        this.permissionsService.deleteClientRole(roleToDelete.id).pipe(
          timeout(6000)
        )
      );

      this.sharedService.createNotification('success', `Role "${roleToDelete.policy_name}" deleted successfully!`);

      // Remove from list
      this.roles = this.roles.filter((r) => r.id !== roleToDelete.id);
      this.permissionsService.refreshPolicySummary(this.getParentId());
      this.closeDeleteModal();
    } catch (err: any) {
      console.warn('[RolesComponent] deleteClientRole error:', err);
      // If backend call errored but client still wants to remove or shows message
      const serverMessage =
        err?.error?.message ||
        err?.error?.error ||
        err?.message ||
        'Failed to delete role.';
      this.sharedService.createNotification('error', serverMessage);
      this.closeDeleteModal();
    } finally {
      this.isDeleting = false;
      this.cdr.detectChanges();
    }
  }

  navigateToManage(roleId: number | string, tab?: 'permissions' | 'users'): void {
    const queryParams: any = { roleId };
    if (tab) {
      queryParams.tab = tab;
    }
    this.router.navigate(['/home/roles-management'], { queryParams });
  }

  getRoleDisplayName(role: any): string {
    const rawName = (role?.policy_name || '').toString().trim();
    if (!rawName) return '';

    const lower = rawName.toLowerCase();
    const slug = lower.replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

    const key = `roles-page.role-names.${lower}`;
    const translated = this.translateService.instant(key);
    if (translated && translated !== key) return translated;

    const currentLang = this.translateService.currentLang || localStorage.getItem('lang') || 'en';
    if (currentLang === 'ar') {
      const map: Record<string, string> = {
        'sub_user': 'مستخدم فرعي',
        'subuser': 'مستخدم فرعي',
        'sub user': 'مستخدم فرعي',
        'sub-user': 'مستخدم فرعي',
        'roles management': 'إدارة الأدوار',
        'roles-management': 'إدارة الأدوار',
        'directory': 'الدليل',
        'admin': 'مدير',
        'administrator': 'المسؤول العام',
        'manager': 'مدير',
        'campaign manager': 'مدير الحملات',
        'developer': 'مطور',
        'developer & technical': 'مطور وتقني',
        'editor': 'محرر',
        'viewer': 'مشاهد',
        'auditor': 'مدقق'
      };
      if (map[lower]) return map[lower];
      if (map[slug]) return map[slug];
    }
    return rawName;
  }

  getRoleDisplayDescription(role: any): string {
    const rawDesc = (role?.description || '').toString().trim();
    if (!rawDesc) {
      return this.translateService.instant('roles-page.default-role-desc');
    }

    const currentLang = this.translateService.currentLang || localStorage.getItem('lang') || 'en';
    if (currentLang === 'ar') {
      const lower = rawDesc.toLowerCase();
      if (lower === 'sub_user' || lower === 'subuser' || lower === 'sub user') return 'مستخدم فرعي مخصص للمنصة';
      if (lower === 'description role' || lower === 'description roles') return 'دور مخصص لمنح الصلاحيات للمستخدمين';
      if (lower.startsWith('explicabo')) return 'إدارة صلاحيات وأذونات الوصول لمختلف أقسام المنصة';
      if (lower === 'custom organizational policy defining specific client portal page permissions.') {
        return this.translateService.instant('roles-page.default-role-desc');
      }
      if (lower.includes('unrestricted master access across all platform modules')) {
        return this.translateService.instant('roles-page.preset-admin-desc');
      }
      if (lower.includes('create and manage interactive contests')) {
        return this.translateService.instant('roles-page.preset-manager-desc');
      }
      if (lower.includes('api keys, sdk documentation')) {
        return this.translateService.instant('roles-page.preset-dev-desc');
      }
    }
    return rawDesc;
  }

  private getRoleIcon(name: string, index: number): string {
    const lower = (name || '').toLowerCase();
    if (lower.includes('admin')) return 'fa-solid fa-crown';
    if (lower.includes('campaign') || lower.includes('contest')) return 'fa-solid fa-bullhorn';
    if (lower.includes('developer') || lower.includes('tech')) return 'fa-solid fa-code';
    if (lower.includes('view') || lower.includes('audit')) return 'fa-solid fa-eye';
    if (lower.includes('manager')) return 'fa-solid fa-user-shield';
    const icons = ['fa-solid fa-shield-halved', 'fa-solid fa-users-gear', 'fa-solid fa-palette', 'fa-solid fa-chart-line'];
    return icons[index % icons.length];
  }

  private getRoleColor(index: number): string {
    const colors = ['#7928CA', '#0284C7', '#10B981', '#F59E0B', '#6366F1', '#EC4899'];
    return colors[index % colors.length];
  }
}
